// FAQ Database for Support & Help section
export const faqCategories = [
  {
    id: 'getting_started',
    name: 'Getting Started',
    icon: '🚀',
    faqs: [
      {
        id: 'what_is_healthloop',
        question: 'What is HealthLoop and how does it work?',
        answer: 'HealthLoop is a preventive health intelligence platform that helps you track your daily wellness, manage medications, access personalized nutrition plans, and get health insights. Simply complete your daily check-in, track your habits, and let our AI-powered system provide personalized recommendations to improve your health.'
      },
      {
        id: 'how_to_start',
        question: 'How do I get started with HealthLoop?',
        answer: 'After creating your account, complete the onboarding questionnaire to set your health goals. Then, start your first daily wellness check-in by clicking "Daily Wellness" on your dashboard. Track your sleep, water intake, stress levels, and more to start building your health profile.'
      },
      {
        id: 'daily_checkin',
        question: 'How often should I complete check-ins?',
        answer: 'We recommend completing daily check-ins to get the most accurate health insights. However, you can check in as frequently as you like. Consistent tracking helps our system detect patterns and provide better personalized recommendations.'
      }
    ]
  },
  {
    id: 'account_subscription',
    name: 'Account & Subscription',
    icon: '👤',
    faqs: [
      {
        id: 'free_vs_premium',
        question: 'What\'s the difference between Free and Premium?',
        answer: 'Free users get access to daily wellness check-ins, basic health tracking, and medication reminders. Premium users unlock Smart Nutrition meal plans, Expert Support with document uploads, advanced health insights, priority support, and personalized meal delivery options.'
      },
      {
        id: 'cancel_subscription',
        question: 'How do I cancel my subscription?',
        answer: 'You can cancel your subscription anytime from Settings > Subscription. Click "Manage Subscription" and follow the cancellation process. You\'ll retain Premium access until the end of your current billing period.'
      },
      {
        id: 'data_privacy',
        question: 'Is my health data secure and private?',
        answer: 'Yes. We take your privacy seriously. All health data is encrypted, stored securely, and never shared with third parties without your explicit consent. You can view our Privacy Policy in Settings for complete details.'
      },
      {
        id: 'change_email',
        question: 'How do I change my email or password?',
        answer: 'Go to Settings > Profile to update your email address. To change your password, use the "Forgot Password" link on the login page or contact support for assistance.'
      }
    ]
  },
  {
    id: 'health_tracking',
    name: 'Health Tracking',
    icon: '💚',
    faqs: [
      {
        id: 'wellness_score',
        question: 'How is my Wellness Score calculated?',
        answer: 'Your Wellness Score is calculated based on multiple factors including sleep quality, water intake, exercise, stress levels, and check-in consistency. The score updates daily and ranges from 0-100, with higher scores indicating better overall wellness habits.'
      },
      {
        id: 'missed_checkin',
        question: 'What happens if I miss a check-in?',
        answer: 'Missing occasional check-ins won\'t affect your data significantly. However, consistent tracking provides better health insights. You cannot backfill past check-ins, so try to check in daily for the most accurate trends.'
      },
      {
        id: 'medication_reminders',
        question: 'How do medication reminders work?',
        answer: 'Add your medications in the Medication Support section. Set your preferred reminder times, and we\'ll notify you when it\'s time to take your medication. You can mark doses as taken directly from the notification or the medications page.'
      },
      {
        id: 'health_insights',
        question: 'What are Health Insights and how do I use them?',
        answer: 'Health Insights analyze your check-in patterns to detect trends and potential health concerns. They appear on your dashboard when our system identifies patterns like poor sleep, low energy, or high stress. Click on any insight to see detailed recommendations.'
      }
    ]
  },
  {
    id: 'smart_nutrition',
    name: 'Smart Nutrition & Meals',
    icon: '🥗',
    faqs: [
      {
        id: 'customize_meals',
        question: 'How do I customize my meal preferences?',
        answer: 'Go to Smart Nutrition and click "Customise Meals". Set your cuisine preferences, dietary restrictions (vegan, gluten-free, etc.), allergies, and any foods you dislike. Our AI will personalize meal recommendations based on your preferences.'
      },
      {
        id: 'swap_meals',
        question: 'Can I swap meals I don\'t like?',
        answer: 'Yes! Click the "Swap Meal" icon on any meal card to see alternative options that match your preferences and dietary needs. You can swap meals as many times as you like.'
      },
      {
        id: 'nearby_options',
        question: 'How does "Nearby Healthy Options" work?',
        answer: 'This feature uses your browser\'s location to find healthy restaurants and cafes near you. You\'ll need to grant location permission when prompted. We show options within a 5km radius with ratings and directions.'
      },
      {
        id: 'meal_delivery',
        question: 'How do I order meal delivery?',
        answer: 'Browse Smart Nutrition and click "Order Now" on any meal plan. Choose your delivery address and frequency (daily, weekly, or monthly). Premium subscribers get exclusive discounts on meal delivery services.'
      }
    ]
  },
  {
    id: 'technical_issues',
    name: 'Technical Issues',
    icon: '⚙️',
    faqs: [
      {
        id: 'app_not_loading',
        question: 'The app is not loading or showing errors. What should I do?',
        answer: 'First, try refreshing the page (Ctrl+R or Cmd+R). If that doesn\'t work, clear your browser cache and cookies. Still having issues? Try a different browser or use incognito/private mode. If the problem persists, report it via "Report a Problem" below.'
      },
      {
        id: 'login_issues',
        question: 'I can\'t log in to my account',
        answer: 'Verify you\'re using the correct email and password. If you forgot your password, click "Forgot Password" on the login page. Make sure cookies are enabled in your browser. If you continue having issues, contact our support team.'
      },
      {
        id: 'notifications_not_working',
        question: 'I\'m not receiving notifications',
        answer: 'Check your browser notification settings and ensure HealthLoop has permission to send notifications. On mobile, check your device notification settings. You can also verify your notification preferences in Settings > Notifications.'
      },
      {
        id: 'data_not_syncing',
        question: 'My data isn\'t syncing or updating',
        answer: 'This usually happens due to connectivity issues. Check your internet connection and refresh the page. If you\'re on mobile, try switching between WiFi and mobile data. Data should sync automatically within a few seconds.'
      }
    ]
  }
];

// Get all FAQs flattened
export const getAllFAQs = () => {
  return faqCategories.flatMap(category => 
    category.faqs.map(faq => ({
      ...faq,
      category: category.name,
      categoryIcon: category.icon
    }))
  );
};

// Search FAQs
export const searchFAQs = (query) => {
  if (!query || query.trim() === '') return getAllFAQs();
  
  const searchTerm = query.toLowerCase();
  return getAllFAQs().filter(faq => 
    faq.question.toLowerCase().includes(searchTerm) ||
    faq.answer.toLowerCase().includes(searchTerm)
  );
};

// Get FAQ by category
export const getFAQsByCategory = (categoryId) => {
  const category = faqCategories.find(cat => cat.id === categoryId);
  return category ? category.faqs : [];
};
